/**
 * Canonical color utilities for the Cloud Security Platform.
 *
 * SINGLE SOURCE OF TRUTH - replaces 52 inline getSeverityColor/getStatusColor copies.
 *
 * Usage:
 *   import { getSeverityColor, getStatusColor, getRiskColor } from '../shared/utils/colors';
 */

import type { SeverityLevel } from '../types';

// ─── Severity Colors ─────────────────────────────────────────────────

const SEVERITY_COLORS: Record<SeverityLevel, { text: string; bg: string; border: string; badge: string }> = {
  critical: { text: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20', badge: 'text-red-400 bg-red-400/10 border-red-400/20' },
  high:     { text: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/20', badge: 'text-orange-400 bg-orange-400/10 border-orange-400/20' },
  medium:   { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/20', badge: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
  low:      { text: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/20', badge: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
  info:     { text: 'text-gray-400', bg: 'bg-gray-400/10', border: 'border-gray-400/20', badge: 'text-gray-400 bg-gray-400/10 border-gray-400/20' },
};

/** Get full badge class string for a severity level. */
export function getSeverityColor(severity: string): string {
  const s = severity?.toLowerCase() as SeverityLevel;
  return SEVERITY_COLORS[s]?.badge ?? SEVERITY_COLORS.info.badge;
}

/** Get just the text color for a severity level. */
export function getSeverityTextColor(severity: string): string {
  const s = severity?.toLowerCase() as SeverityLevel;
  return SEVERITY_COLORS[s]?.text ?? SEVERITY_COLORS.info.text;
}

/** Get just the background color for a severity level. */
export function getSeverityBgColor(severity: string): string {
  const s = severity?.toLowerCase() as SeverityLevel;
  return SEVERITY_COLORS[s]?.bg ?? SEVERITY_COLORS.info.bg;
}

// ─── Status Colors ───────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  open: 'text-red-400 bg-red-400/10 border-red-400/20',
  in_progress: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  investigating: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  acknowledged: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  resolved: 'text-green-400 bg-green-400/10 border-green-400/20',
  suppressed: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
  false_positive: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
  active: 'text-green-400 bg-green-400/10 border-green-400/20',
  inactive: 'text-gray-400 bg-gray-400/10 border-gray-400/20',
  error: 'text-red-400 bg-red-400/10 border-red-400/20',
  pending: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  scanning: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20',
  compliant: 'text-green-400 bg-green-400/10 border-green-400/20',
  non_compliant: 'text-red-400 bg-red-400/10 border-red-400/20',
};

/** Get badge class string for a status value. */
export function getStatusColor(status: string): string {
  const s = status?.toLowerCase().replace(/ /g, '_');
  return STATUS_COLORS[s] ?? 'text-gray-400 bg-gray-400/10 border-gray-400/20';
}

// ─── Risk Score Colors ───────────────────────────────────────────────

/** Get text color based on risk score (0-100). */
export function getRiskColor(score: number): string {
  if (score >= 80) return 'text-red-400';
  if (score >= 60) return 'text-orange-400';
  if (score >= 40) return 'text-yellow-400';
  return 'text-green-400';
}

/** Get background color based on risk score (0-100). */
export function getRiskBgColor(score: number): string {
  if (score >= 80) return 'bg-red-400/10 border-red-400/20';
  if (score >= 60) return 'bg-orange-400/10 border-orange-400/20';
  if (score >= 40) return 'bg-yellow-400/10 border-yellow-400/20';
  return 'bg-green-400/10 border-green-400/20';
}

/** Get full badge class for risk score. */
export function getRiskBadgeColor(score: number): string {
  return `${getRiskColor(score)} ${getRiskBgColor(score)}`;
}

// ─── Provider Colors ─────────────────────────────────────────────────

const PROVIDER_COLORS: Record<string, string> = {
  aws: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  azure: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  gcp: 'text-green-400 bg-green-400/10 border-green-400/20',
  oci: 'text-red-400 bg-red-400/10 border-red-400/20',
  alibaba: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
};

/** Get badge class for cloud provider. */
export function getProviderColor(provider: string): string {
  return PROVIDER_COLORS[provider?.toLowerCase()] ?? 'text-gray-400 bg-gray-400/10 border-gray-400/20';
}

// ─── Severity Pill/Dot/Bar Styles (Tailwind classes) ─────────────────
// Replaces per-component SEV_STYLES constants.

export const SEV_STYLES: Record<string, { pill: string; dot: string; bar: string }> = {
  critical:      { pill: 'bg-red-500/20 text-red-400 border-red-500/40',        dot: 'bg-red-500',    bar: 'bg-red-500' },
  high:          { pill: 'bg-orange-500/20 text-orange-400 border-orange-500/40', dot: 'bg-orange-500', bar: 'bg-orange-500' },
  medium:        { pill: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40', dot: 'bg-yellow-500', bar: 'bg-yellow-500' },
  low:           { pill: 'bg-blue-500/20 text-blue-400 border-blue-500/40',      dot: 'bg-blue-500',   bar: 'bg-blue-500' },
  info:          { pill: 'bg-gray-500/20 text-gray-400 border-gray-500/40',      dot: 'bg-gray-500',   bar: 'bg-gray-500' },
  informational: { pill: 'bg-gray-500/20 text-gray-400 border-gray-500/40',      dot: 'bg-gray-500',   bar: 'bg-gray-500' },
};

// ─── Hex Colors (for inline style= usage) ────────────────────────────
// Replaces 53+ inline `severityColor` functions across components.
//
// Usage:
//   import { severityHex, statusHex } from '../../shared/utils/colors';
//   <span style={{ color: severityHex(finding.severity) }}>...</span>

const SEVERITY_HEX: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#2563eb',
  info: '#6b7280',
};

/** Get hex color for severity (inline style usage). */
export function severityHex(severity: string): string {
  return SEVERITY_HEX[severity?.toLowerCase()] || '#6b7280';
}

const STATUS_HEX: Record<string, string> = {
  open: '#dc2626',
  in_progress: '#d97706',
  investigating: '#d97706',
  resolved: '#16a34a',
  suppressed: '#6b7280',
  false_positive: '#6b7280',
  active: '#16a34a',
  inactive: '#6b7280',
  error: '#dc2626',
  pending: '#2563eb',
  scanning: '#06b6d4',
  compliant: '#16a34a',
  non_compliant: '#dc2626',
};

/** Get hex color for status (inline style usage). */
export function statusHex(status: string): string {
  return STATUS_HEX[status?.toLowerCase().replace(/ /g, '_')] || '#6b7280';
}

const SEVERITY_BG_HEX: Record<string, string> = {
  critical: 'rgba(220, 38, 38, 0.15)',
  high: 'rgba(234, 88, 12, 0.15)',
  medium: 'rgba(217, 119, 6, 0.15)',
  low: 'rgba(37, 99, 235, 0.15)',
  info: 'rgba(107, 114, 128, 0.15)',
};

/** Get background hex color for severity. */
export function severityBgHex(severity: string): string {
  return SEVERITY_BG_HEX[severity?.toLowerCase()] || 'rgba(107, 114, 128, 0.15)';
}
